import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { isEmail, validateLoginForm } from "../utils/validators";

export default function Login() {
  const {
    loginWithOtp,
    getLoginOptions,
    requestLoginOtp
  } = useContext(AuthContext);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: "",
    name: "",
    otp: ""
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(null);
  const [hasCheckedEmail, setHasCheckedEmail] = useState(false);
  const [checkingEmailStatus, setCheckingEmailStatus] = useState(false);
  const [hasRequestedOtp, setHasRequestedOtp] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [sendingOtp, setSendingOtp] = useState(false);
  const latestLookupRef = useRef(0);
  const otpIntervalRef = useRef(null);

  const normalizedEmail = useMemo(() => form.email.trim().toLowerCase(), [form.email]);

  useEffect(
    () => () => {
      if (otpIntervalRef.current) {
        window.clearInterval(otpIntervalRef.current);
      }
    },
    []
  );

  const clearMessages = () => {
    setSubmitError("");
    setSuccessMessage("");
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setForm((previous) =>
      field === "email"
        ? { ...previous, email: value, otp: "", name: "" }
        : { ...previous, [field]: value }
    );
    setErrors((previous) => ({ ...previous, [field]: "" }));
    clearMessages();

    if (field === "email") {
      latestLookupRef.current += 1;
      setIsExistingUser(null);
      setHasCheckedEmail(false);
      setCheckingEmailStatus(false);
      setHasRequestedOtp(false);
      setOtpCooldown(0);
      if (otpIntervalRef.current) {
        window.clearInterval(otpIntervalRef.current);
      }
    }
  };

  const loadLoginOptions = useCallback(async (
    emailValue = form.email,
    { showMessages = true, showErrors = true } = {}
  ) => {
    const normalizedInput = String(emailValue || "").trim().toLowerCase();

    if (!isEmail(normalizedInput)) {
      if (showErrors) {
        setErrors((previous) => ({ ...previous, email: "Enter a valid email address." }));
      }
      return { ok: false };
    }

    const lookupId = latestLookupRef.current + 1;
    latestLookupRef.current = lookupId;
    setCheckingEmailStatus(true);

    const result = await getLoginOptions(normalizedInput);
    if (lookupId !== latestLookupRef.current) {
      return { ok: false, stale: true };
    }

    setCheckingEmailStatus(false);

    if (!result.ok) {
      if (showMessages) {
        setSubmitError(result.error);
      }
      return { ok: false };
    }

    setHasCheckedEmail(true);
    setIsExistingUser(result.existingUser);
    setErrors((previous) => ({ ...previous, email: "" }));
    if (result.existingUser) {
      if (showMessages) {
        setSuccessMessage("Existing account found. Enter OTP to login.");
      }
    } else {
      if (showMessages) {
        setSuccessMessage("New email detected. Enter name, request OTP, and login.");
      }
    }

    return { ok: true, existingUser: result.existingUser };
  }, [form.email, getLoginOptions]);

  useEffect(() => {
    if (!isEmail(form.email)) {
      setHasCheckedEmail(false);
      setCheckingEmailStatus(false);
      setIsExistingUser(null);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      loadLoginOptions(form.email, { showMessages: false, showErrors: false });
    }, 450);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [form.email, loadLoginOptions]);

  const handleGetOtp = async () => {
    if (otpCooldown > 0 || sendingOtp) {
      return;
    }

    clearMessages();

    let resolvedExistingUser = isExistingUser;
    if (resolvedExistingUser === null) {
      const optionsResult = await loadLoginOptions(form.email);
      if (!optionsResult.ok) {
        return;
      }
      resolvedExistingUser = optionsResult.existingUser;
    }

    setSendingOtp(true);
    const result = await requestLoginOtp({
      email: normalizedEmail,
      name: ""
    });
    setSendingOtp(false);

    if (!result.ok) {
      setSubmitError(result.error);
      return;
    }

    setSuccessMessage(result.message);
    setHasRequestedOtp(true);
    setOtpCooldown(30);

    otpIntervalRef.current = window.setInterval(() => {
      setOtpCooldown((previous) => {
        if (previous <= 1) {
          if (otpIntervalRef.current) {
            window.clearInterval(otpIntervalRef.current);
          }
          return 0;
        }
        return previous - 1;
      });
    }, 1000);
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    if (loggingIn) {
      return;
    }

    let resolvedExistingUser = isExistingUser;
    if (resolvedExistingUser === null && !hasCheckedEmail) {
      const optionsResult = await loadLoginOptions(form.email);
      if (!optionsResult.ok) {
        return;
      }
      resolvedExistingUser = optionsResult.existingUser;
    }

    const nextErrors = validateLoginForm({
      email: form.email,
      otp: form.otp,
      name: form.name,
      isExistingUser: Boolean(resolvedExistingUser)
    });

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setLoggingIn(true);
    const result = await loginWithOtp({
      email: normalizedEmail,
      otp: form.otp,
      name: resolvedExistingUser ? "" : form.name
    });
    setLoggingIn(false);

    if (!result.ok) {
      setSubmitError(result.error);
      return;
    }

    navigate(resolvedExistingUser ? "/dashboard" : "/author-details");
  };

  const otpButtonLabel = sendingOtp
    ? "Sending..."
    : otpCooldown > 0
      ? `${otpCooldown}s`
      : hasRequestedOtp
        ? "Resend OTP"
        : "Get OTP";

  return (
    <section className="auth-page-shell">
      <form className="auth-box" onSubmit={handleLogin} noValidate>
        <h2>Login</h2>

      <div className="auth-input-with-action">
        <input
          placeholder="Email Address"
          value={form.email}
          onChange={handleChange("email")}
          aria-invalid={Boolean(errors.email)}
        />
        {checkingEmailStatus && (
          <span className="email-status loading">
            <span className="status-spinner" />
          </span>
        )}
        {!checkingEmailStatus && hasCheckedEmail && isExistingUser && (
          <span className="email-status verified">Verified</span>
        )}
      </div>
      {errors.email && <p className="input-error">{errors.email}</p>}

      <div className="otp-row">
        <input
          placeholder="Enter 6-digit OTP"
          value={form.otp}
          onChange={handleChange("otp")}
          maxLength={6}
          aria-invalid={Boolean(errors.otp)}
        />
        <button
          type="button"
          className="btn-small"
          onClick={handleGetOtp}
          disabled={sendingOtp || otpCooldown > 0}
        >
          {otpButtonLabel}
        </button>
      </div>
      {errors.otp && <p className="input-error">{errors.otp}</p>}

      {hasCheckedEmail && isExistingUser === false && (
        <>
          <input
            placeholder="Name"
            value={form.name}
            onChange={handleChange("name")}
            aria-invalid={Boolean(errors.name)}
          />
          {errors.name && <p className="input-error">{errors.name}</p>}
        </>
      )}

      {submitError && <p className="submit-error">{submitError}</p>}
      {successMessage && <p className="submit-success">{successMessage}</p>}

        <button type="submit" disabled={loggingIn || checkingEmailStatus}>
          {loggingIn ? "Logging in..." : "Login"}
        </button>
      </form>
    </section>
  );
}
